/**
 * Desk — Settings → AI access.
 *
 * Where an employee connects their own AI client to their own Desk: create a
 * personal token, copy the one command that wires it up, revoke it when done.
 * The token value exists once, in the response that created it, so the panel
 * that shows it is deliberately loud and cannot be reopened.
 */
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Subheading } from "catalyst-ui-kit/heading";
import { Text } from "catalyst-ui-kit/text";
import { Badge } from "catalyst-ui-kit/badge";
import {
  fetchApiTokens,
  createApiToken,
  revokeApiToken,
  MCP_ENDPOINT_URL,
} from "services/api-tokens.service";
import { copyTextToClipboard } from "containers/share-board-form/share-board-link";
import { ApiTokenEntry, ApiTokenScope } from "types/ApiToken";
import { formatPrettyDate } from "utils/helperFunctions";

// On a phone fields are 16px (iOS zooms into anything smaller) and buttons 44px tall.
const inputCls =
  "rounded-lg border border-zinc-300 bg-transparent px-3 py-1.5 text-sm outline-none transition-all duration-150 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:border-zinc-700 dark:focus:ring-indigo-400/20 dark:focus:border-indigo-400 max-md:min-h-11 max-md:text-base";
const btnCls =
  "rounded-lg border border-zinc-200/80 bg-transparent px-3 py-1.5 text-sm font-medium hover:bg-zinc-50 dark:border-zinc-800 dark:hover:bg-zinc-900 transition-colors duration-150 disabled:opacity-40 max-md:min-h-11";
const primaryBtnCls =
  "rounded-lg bg-indigo-600 px-3.5 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 active:bg-indigo-700 shadow-sm transition-colors duration-150 disabled:opacity-40 dark:bg-indigo-500 dark:hover:bg-indigo-400 max-md:min-h-11";

function connectCommand(token: string): string {
  return `claude mcp add --transport http erp ${MCP_ENDPOINT_URL} --header "Authorization: Bearer ${token}"`;
}

/**
 * The one and only sight of a new token, rendered by the settings page ABOVE
 * the tabs: switching tab unmounts this tab's body, and a token that is gone is
 * gone — there is no endpoint that can show it again.
 */
export function FreshTokenPanel({ token, onDismiss }: { token: string; onDismiss: () => void }) {
  const { t } = useTranslation();
  const copy = async (text: string, message: string) => {
    const ok = await copyTextToClipboard(text);
    if (ok) toast.success(message);
    else toast.error(t("api_token_copy_failed"));
  };

  return (
    <div className="mt-4 rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-4 space-y-3">
      <div className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">
        {t("api_token_created_once")}
      </div>
      <div className="flex items-center gap-2">
        <code className="flex-1 break-all rounded-lg bg-white px-3 py-2 text-xs border border-zinc-200 dark:bg-zinc-950 dark:border-zinc-800">
          {token}
        </code>
        <button type="button" className={btnCls} onClick={() => copy(token, t("api_token_copied"))}>
          {t("api_token_copy")}
        </button>
      </div>
      <div className="text-xs text-zinc-600 dark:text-zinc-400">{t("api_token_connect_hint")}</div>
      <div className="flex items-center gap-2">
        <code className="flex-1 break-all rounded-lg bg-white px-3 py-2 text-xs border border-zinc-200 dark:bg-zinc-950 dark:border-zinc-800">
          {connectCommand(token)}
        </code>
        <button
          type="button"
          className={btnCls}
          onClick={() => copy(connectCommand(token), t("api_token_command_copied"))}
        >
          {t("api_token_copy")}
        </button>
      </div>
      <button type="button" className={btnCls} onClick={onDismiss}>
        {t("api_token_hide")}
      </button>
    </div>
  );
}

export default function ApiTokensTab({
  companyId,
  onCreated,
}: {
  companyId: string;
  onCreated: (token: string) => void;
}) {
  const { t } = useTranslation();
  const [entries, setEntries] = useState<ApiTokenEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [name, setName] = useState("");
  const [canWrite, setCanWrite] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const errText = useCallback(
    (e: any) => e?.response?.data?.message || e?.message || t("desk_action_failed"),
    [t]
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetchApiTokens(companyId);
      setEntries(res.entries);
    } catch (e: any) {
      toast.error(errText(e));
    } finally {
      setIsLoading(false);
    }
  }, [companyId, errText]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    if (!name.trim() || isCreating) return;
    setIsCreating(true);
    try {
      const scopes: ApiTokenScope[] = canWrite ? ["desk:read", "desk:write"] : ["desk:read"];
      const res = await createApiToken(companyId, { name: name.trim(), scopes });
      onCreated(res.token);
      setName("");
      setCanWrite(false);
      await load();
    } catch (e: any) {
      toast.error(errText(e));
    } finally {
      setIsCreating(false);
    }
  };

  const revoke = async (entry: ApiTokenEntry) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t("api_token_revoke_confirm", { name: entry.name }))) return;
    try {
      await revokeApiToken(companyId, entry.id);
      toast.success(t("api_token_revoked"));
      await load();
    } catch (e: any) {
      toast.error(errText(e));
    }
  };


  return (
    <div className="mt-6 space-y-6">
      <div>
        <Subheading>{t("api_token_heading")}</Subheading>
        <Text className="mt-1 max-w-3xl">{t("api_token_intro")}</Text>
      </div>

      <div className="rounded-xl border border-zinc-200 p-4 space-y-3 dark:border-zinc-800">
        <Subheading level={3}>{t("api_token_new")}</Subheading>
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("api_token_name_placeholder")}
            aria-label={t("api_token_name")}
            className={inputCls + " w-full sm:w-auto sm:min-w-[16rem]"}
          />
          <button type="button" onClick={create} disabled={!name.trim() || isCreating} className={primaryBtnCls}>
            {isCreating ? t("api_token_creating") : t("api_token_create")}
          </button>
        </div>
        <label className="flex items-start gap-2 text-sm max-md:min-h-11">
          <input
            type="checkbox"
            checked={canWrite}
            onChange={(e) => setCanWrite(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-zinc-300 text-indigo-600"
          />
          <span>
            <span className="font-medium">{t("api_token_scope_write")}</span>
            <span className="block text-xs text-amber-700 dark:text-amber-400">{t("api_token_scope_write_warning")}</span>
          </span>
        </label>
      </div>

      <div className="rounded-xl border border-zinc-200 dark:border-zinc-800">
        <div className="border-b border-zinc-200 px-4 py-2 text-sm font-medium dark:border-zinc-800">
          {t("api_token_active")}
        </div>
        {isLoading ? (
          <div className="p-4 text-sm text-zinc-500">{t("loading")}</div>
        ) : entries.length === 0 ? (
          <div className="p-4 text-sm text-zinc-500">{t("api_token_none")}</div>
        ) : (
          <ul className="divide-y divide-zinc-100 dark:divide-zinc-900">
            {entries.map((entry) => (
              <li key={entry.id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
                <span className="font-medium">{entry.name}</span>
                <code className="text-xs text-zinc-500">••••{entry.tokenHint}</code>
                {entry.scopes.split(",").map((s) => (
                  <Badge key={s} color={s === "desk:write" ? "amber" : "zinc"}>
                    {s}
                  </Badge>
                ))}
                <span className="text-xs text-zinc-500">
                  {t("api_token_created_at", { date: formatPrettyDate(entry.createdAt, "lt-LT", true) })}
                </span>
                <span className="text-xs text-zinc-500">
                  {entry.lastUsedAt
                    ? t("api_token_last_used", { date: formatPrettyDate(entry.lastUsedAt, "lt-LT", true) })
                    : t("api_token_never_used")}
                </span>
                <button type="button" onClick={() => revoke(entry)} className={btnCls + " ml-auto"}>
                  {t("api_token_revoke")}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
